import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../../v2-theme.css';

function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const paymentData = location.state || {};
  const { type, amount, billingCycle, message } = paymentData;

  // Configuration based on payment type
  const getConfig = () => {
    const configs = {
      worker_professional: {
        message: message || 'Welcome to Professional!',
        planName: 'Worker Professional',
        amount: billingCycle === 'annual' ? '$99/year' : '$9.99/month',
        billing: billingCycle === 'annual' ? 'Billed annually • Save $20.88/year' : 'Billed monthly',
        dashboardLink: '/dashboard/worker',
        profileLink: '/profile/worker',
        nextSteps: [
          'Upload your professional photo',
          'Add your custom description',
          'Upload your CV/Resume',
          'Add multiple positions',
          'Display your ServSafe certification',
          'Start getting priority visibility'
        ]
      },
      restaurant_unlimited: {
        message: message || (billingCycle === 'annual' ? 'Welcome to Unlimited - Annual Plan!' : 'Welcome to Unlimited!'),
        planName: 'Restaurant Unlimited',
        amount: billingCycle === 'annual' ? '$999/year' : '$99/month',
        billing: billingCycle === 'annual' ? 'Billed annually • Save $189/year' : 'Billed monthly',
        dashboardLink: '/dashboard/restaurant',
        profileLink: '/profile/restaurant',
        nextSteps: [
          'Post unlimited shifts at no extra cost',
          'Access your enhanced profile',
          'Get priority listing in worker searches',
          'View advanced analytics',
          'Contact dedicated support team',
          'Access early beta features'
        ]
      },
      shift_payment: {
        message: message || 'Shift Payment Complete!',
        planName: 'Single Shift Payment',
        amount: `$${amount || 19}.00`,
        billing: 'One-time payment',
        dashboardLink: '/dashboard/restaurant',
        profileLink: '/dashboard/restaurant',
        nextSteps: [
          'Your shift is now posted',
          'Workers can now view and claim it',
          "You'll receive notifications when claimed",
          'Message workers directly through the platform',
          'Track shift status in your dashboard'
        ]
      }
    };

    return configs[type] || configs.worker_professional;
  };

  const config = getConfig();

  // If no payment data, redirect to appropriate dashboard
  useEffect(() => {
    if (!type) {
      const dashboardPath = user?.user_type === 'worker' ? '/dashboard/worker' : '/dashboard/restaurant';
      navigate(dashboardPath);
    }
  }, [type, user, navigate]);

  return (
    <div className="v2-page-wrapper" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
      <div style={{ maxWidth: '600px', width: '100%', textAlign: 'center' }}>
        {/* Success Icon */}
        <div style={{
          width: '100px',
          height: '100px',
          background: '#4CAF50',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 30px',
          fontSize: '3rem',
          animation: 'scaleIn 0.5s ease-out'
        }}>
          ✓
        </div>

        <h1 className="v2-heading-xl" style={{ marginBottom: '15px' }}>
          Payment Successful!
        </h1>
        <p className="v2-text-muted" style={{ fontSize: '1.1rem', marginBottom: '40px' }}>
          {config.message}
        </p>

        {/* Payment Details Card */}
        <div className="v2-card" style={{ padding: '30px', marginBottom: '30px', textAlign: 'left' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0', borderBottom: '1px solid var(--dark-steel)' }}>
            <span style={{ color: 'var(--silver)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Plan
            </span>
            <span style={{ fontWeight: '600', fontSize: '1rem' }}>
              <span style={{
                background: 'var(--steel)',
                color: 'var(--charcoal)',
                padding: '4px 10px',
                fontSize: '0.8rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {config.planName}
              </span>
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0', borderBottom: '1px solid var(--dark-steel)' }}>
            <span style={{ color: 'var(--silver)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Amount
            </span>
            <span style={{ fontWeight: '600', fontSize: '1rem' }}>
              {config.amount}
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0', borderBottom: '1px solid var(--dark-steel)' }}>
            <span style={{ color: 'var(--silver)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Billing
            </span>
            <span style={{ fontWeight: '600', fontSize: '1rem' }}>
              {config.billing}
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0' }}>
            <span style={{ color: 'var(--silver)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Status
            </span>
            <span style={{ fontWeight: '600', fontSize: '1rem', color: '#4CAF50' }}>
              Active
            </span>
          </div>
        </div>

        {/* Next Steps */}
        <div style={{
          background: 'rgba(76, 175, 80, 0.1)',
          border: '1px solid #4CAF50',
          padding: '25px',
          borderRadius: '2px',
          marginBottom: '30px',
          textAlign: 'left'
        }}>
          <h3 className="v2-heading-md" style={{ marginBottom: '15px', color: '#4CAF50' }}>
            What's Next?
          </h3>
          <ul style={{ listStyle: 'none', paddingLeft: '0' }}>
            {config.nextSteps.map((step, index) => (
              <li key={index} style={{ padding: '8px 0', paddingLeft: '25px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#4CAF50', fontWeight: '600' }}>✓</span>
                {step}
              </li>
            ))}
          </ul>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate(config.dashboardLink)}
            className="v2-btn v2-btn-primary"
            style={{ minWidth: '180px' }}
          >
            Go to Dashboard
          </button>
          <button
            onClick={() => navigate(config.profileLink)}
            className="v2-btn v2-btn-secondary"
            style={{ minWidth: '180px' }}
          >
            View Profile
          </button>
        </div>

        {/* Support Info */}
        <div style={{
          marginTop: '30px',
          paddingTop: '30px',
          borderTop: '1px solid var(--dark-steel)',
          color: 'var(--silver)',
          fontSize: '0.9rem'
        }}>
          Questions? Contact us at <strong style={{ color: 'var(--white)' }}>support@handsonthefly.com</strong><br />
          Your receipt has been sent to your email address.
        </div>
      </div>

      {/* Add animation keyframes */}
      <style>{`
        @keyframes scaleIn {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default PaymentSuccess;
